import React, { useEffect, useMemo, useState } from "react";
import TimeOfDayBadge from "@/components/TimeOfDayBadge";
import LogReminderControl from "@/components/LogReminderControl";
import N8Emblem from "@/components/N8Emblem";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import {
  Sunrise, Dumbbell, Syringe, HeartPulse, Trash2, Plus, Save, Circle,
  Sparkles, Droplet, Moon, X, Pencil, Bell,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import LongevityMilestones from "@/components/LongevityMilestones";
import SmartEntryModal from "@/components/SmartEntryModal";
import SmartEntryRecent from "@/components/SmartEntryRecent";
import { N8 } from "@/components/BrandMark";
import PageHero from "@/components/PageHero";
import { HERO } from "@/lib/heroImages";

// Small helpers used by the header's Smart Entry panel.
function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function yesterdayIso() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const TABS = [
  { key: "routine",    label: "Daily Routine",      icon: Sunrise,    accent: "moss" },
  { key: "fitness",    label: "Fitness",            icon: Dumbbell,   accent: "clay" },
  { key: "peptides",   label: "Peptides",           icon: Syringe,    accent: "terracotta" },
  { key: "procedures", label: "Procedures",         icon: HeartPulse, accent: "clay" },
];

const CAT_ICON = { peptides: Syringe, iv_therapy: Droplet, aesthetic: Sparkles, other: Circle };

export default function Tracking() {
  const [tab, setTab] = useState(() => {
    try { return sessionStorage.getItem("tracking-tab") || "routine"; } catch { return "routine"; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("tracking-tab", tab); } catch { /* noop */ }
  }, [tab]);

  // Load profile just for Pro-gating the Becoming block.
  const [profile, setProfile] = useState(null);
  const [smartOpen, setSmartOpen] = useState(false);
  const [smartInitialDate, setSmartInitialDate] = useState(null);
  const [smartSavedTick, setSmartSavedTick] = useState(0);
  const loadProfile = async () => {
    try { const { data } = await endpoints.profile(); setProfile(data); } catch { /* noop */ }
  };
  useEffect(() => { loadProfile(); }, []);

  // If the user landed here from the Community milestone nudge (or any
  // /tracking#milestones link), gently scroll the milestones block into view
  // once it has rendered. A short delay lets the tab layout settle first.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#milestones") return;
    const t = setTimeout(() => {
      const el = document.querySelector("[data-testid='longevity-milestones']");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="space-y-10" data-testid="tracking-page">
      <PageHero
        emblem=""
        eyebrow={
          <span className="inline-flex items-center gap-3">
            <span>Self Care</span>
            <span className="opacity-50" aria-hidden>·</span>
            <TimeOfDayBadge testid="tracking-time-of-day" />
          </span>
        }
        title={<>Your Innate Potential, <span className="italic">In Motion.</span></>}
        body="Your greatest potential is already within you. Every intentional action helps bring it forward."
        image={HERO.tracking}
        testid="tracking-hero"
      />

      <header className="reveal">
        {/* N8 Smart Entry — styled to match the other N8 feature boxes
            (sand-tinted panel with a small terracotta eyebrow, paragraph,
            action row, and a soft trail of what N8 just organized). */}
        <div
          className="p-5 md:p-6 rounded-md bg-[hsl(var(--sand))]/40 border hairline"
          data-testid="smart-entry-panel"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--terracotta))]">
                <Sparkles size={12} />
                <span><N8 /> Smart Entry</span>
                <span className="text-[10px] rounded-full bg-[hsl(var(--terracotta))]/12 px-2 py-0.5 tracking-[0.18em]">Pro</span>
              </div>
              <p className="mt-2 text-sm text-[hsl(var(--fg))]/85 leading-relaxed max-w-2xl">
                Tell <N8 /> everything you did in one message — movement, hydration, supplements, treatments,
                skincare, sleep — and it will route each detail into the right tracker for you. One entry,
                many rooms, zero repetition.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => { setSmartInitialDate(yesterdayIso()); setSmartOpen(true); }}
                data-testid="smart-entry-open-yesterday"
                className="text-sm rounded-full px-3.5 py-2 border border-[hsl(var(--border))] text-[hsl(var(--fg))] hover:bg-[hsl(var(--paper))] tab-link"
              >
                Log yesterday
              </button>
              <button
                type="button"
                onClick={() => { setSmartInitialDate(todayIso()); setSmartOpen(true); }}
                data-testid="smart-entry-open"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-[hsl(var(--terracotta))] text-white hover:bg-[hsl(var(--terracotta))]/90 shadow-sm tab-link"
              >
                <Sparkles size={14} />
                <span>Open Smart Entry</span>
              </button>
            </div>
          </div>

          <SmartEntryRecent refreshKey={smartSavedTick} testid="smart-entry-recent" />
        </div>
      </header>

      {/* Primary My Journey experience — tracking tabs that let the
          member navigate their journey. Static N8ONE emblem sits above
          the tabs to mark this as a major N8ONE experience rather than
          decorating the hero. */}
      <div className="flex items-center justify-center" data-testid="tracking-emblem-wrap">
        <N8Emblem
          mode="full"
          size={54}
          ariaLabel="N8ONE emblem"
          testid="tracking-emblem"
        />
      </div>
      <nav className="grid grid-cols-2 md:grid-cols-4 gap-3 reveal reveal-1" data-testid="tracking-tabs">
        {TABS.map(({ key, label, icon: Icon, accent }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`tracking-tab-${key}`}
              aria-pressed={active}
              className={`group flex items-center gap-3 p-5 border rounded-md text-left tab-link transition-all ${
                active
                  ? `bg-[hsl(var(--${accent}))]/12 border-[hsl(var(--${accent}))]/50 shadow-[inset_3px_0_0_hsl(var(--${accent}))]`
                  : "hairline hover:bg-[hsl(var(--sand))]/40 bg-[hsl(var(--paper))]"
              }`}
            >
              <span
                className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${
                  active
                    ? `bg-[hsl(var(--${accent}))]/20 text-[hsl(var(--${accent}))]`
                    : "bg-[hsl(var(--sand))] text-[hsl(var(--fg))]"
                }`}
              >
                <Icon size={18} />
              </span>
              <span className="leading-tight">
                <span className="block font-display text-lg">{label}</span>
                <span className="block text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Tracking</span>
              </span>
            </button>
          );
        })}
      </nav>

      <section className="reveal reveal-2" data-testid={`tracking-section-${tab}`}>
        {tab === "routine"    && <DailyRoutineSection />}
        {tab === "fitness"    && <FitnessSection />}
        {tab === "peptides"   && <PeptidesSection />}
        {tab === "procedures" && <ProceduresSection />}
      </section>

      <SmartEntryModal
        open={smartOpen}
        onClose={() => setSmartOpen(false)}
        isPro={Boolean(profile?.is_pro)}
        initialDate={smartInitialDate}
        onSaved={() => setSmartSavedTick((t) => t + 1)}
      />
    </div>
  );
}

/* ================================ DAILY ROUTINE =============================== */
const DRAFT_PREFIX = "daily-routine-draft:";
const FITNESS_DRAFT_KEY = "fitness-add-draft";
const PEPTIDE_DRAFT_KEY = "peptide-add-draft";

// Small helper hook: writes `value` (minus keys in `ignoreKeys`) to localStorage
// whenever it changes. Skips writing if `value` deep-equals `emptyValue`.
function useFormDraft(key, value, setValue, emptyValue, opts = {}) {
  const { active = true } = opts;
  const [restored, setRestored] = useState(false);

  // Read draft once when the form becomes active (e.g., dialog opens)
  useEffect(() => {
    if (!active) return;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const draft = JSON.parse(raw);
      // Only restore if it differs from the current (fresh) form
      if (JSON.stringify(draft) !== JSON.stringify(value)) {
        setValue({ ...emptyValue, ...draft });
        setRestored(true);
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, key]);

  // Autosave: write when non-empty, clear when matches empty
  useEffect(() => {
    if (!active) return;
    try {
      if (JSON.stringify(value) === JSON.stringify(emptyValue)) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(value));
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, active]);

  const clear = () => {
    try { localStorage.removeItem(key); } catch { /* noop */ }
    setRestored(false);
  };

  return { restored, clear, dismissRestored: () => setRestored(false) };
}

function readDraft(date) {
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + date);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeDraft(date, draft) {
  try { localStorage.setItem(DRAFT_PREFIX + date, JSON.stringify(draft)); }
  catch { /* localStorage might be blocked; silently skip */ }
}

function clearDraft(date) {
  try { localStorage.removeItem(DRAFT_PREFIX + date); } catch { /* noop */ }
}


/**
 * HabitReminders — four canonical daily-ritual rows (Skincare, Water, Sleep,
 * Mindfulness) that each carry a bell for setting a soft daily reminder.
 * Uses stable slug ref_ids so a member can revisit and see their existing
 * reminder count for that habit rather than accidentally creating a second
 * per-log entry.
 */
const HABIT_ROWS = [
  { slug: "skincare-daily",    label: "Skincare",    icon: Sparkles, cue: "A quiet nudge for your evening ritual." },
  { slug: "water-daily",       label: "Water",       icon: Droplet,  cue: "A quiet check-in to keep sipping." },
  { slug: "sleep-daily",       label: "Sleep",       icon: Moon,     cue: "A quiet cue to begin your wind-down." },
  { slug: "mindfulness-daily", label: "Mindfulness", icon: Sunrise,  cue: "A pause to breathe and return to yourself." },
];

function HabitReminders() {
  return (
    <div
      className="rounded-md border hairline bg-[hsl(var(--sand))]/25 p-4"
      data-testid="habit-reminders"
    >
      <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--terracotta))] mb-3 inline-flex items-center gap-2">
        <Bell size={11} /> Daily nudges
      </div>
      <ul className="grid sm:grid-cols-2 gap-3">
        {HABIT_ROWS.map((h) => {
          const Icon = h.icon;
          return (
            <li
              key={h.slug}
              className="flex items-center justify-between gap-3 p-3 rounded-md bg-[hsl(var(--paper))] border hairline"
              data-testid={`habit-row-${h.slug}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="grid place-items-center w-8 h-8 rounded-full bg-[hsl(var(--sand))]">
                  <Icon size={14} className="text-[hsl(var(--clay))]" />
                </span>
                <div className="min-w-0">
                  <div className="text-sm">{h.label}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] truncate">{h.cue}</div>
                </div>
              </div>
              <LogReminderControl
                room="habit"
                refId={h.slug}
                refLabel={h.label}
                testid={`habit-reminder-${h.slug}`}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}


function DailyRoutineSection() {
  const today = new Date().toISOString().slice(0, 10);
  const [entryDate, setEntryDate] = useState(today);
  const [supplements, setSupplements] = useState("");
  const [notes, setNotes] = useState("");
  const [savedRef, setSavedRef] = useState({ supplements: "", notes: "" }); // the server's copy
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    setLoaded(false);
    const [{ data: one }, { data: all }] = await Promise.all([
      endpoints.dailyRoutine(entryDate),
      endpoints.listDailyRoutine(14),
    ]);
    const server = { supplements: one.supplements || "", notes: one.notes || "" };
    setSavedRef(server);
    const draft = readDraft(entryDate);
    if (draft && (draft.supplements !== server.supplements || draft.notes !== server.notes)) {
      setSupplements(draft.supplements ?? "");
      setNotes(draft.notes ?? "");
      setDirty(true);
      setRestoredFromDraft(true);
    } else {
      setSupplements(server.supplements);
      setNotes(server.notes);
      setDirty(false);
      setRestoredFromDraft(false);
      clearDraft(entryDate);
    }
    setHistory(all);
    setLoaded(true);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entryDate]);

  // Autosave to localStorage — only after initial load, so we never clobber a
  // stored draft before we've had a chance to restore it.
  useEffect(() => {
    if (!loaded) return;
    if (supplements === savedRef.supplements && notes === savedRef.notes) {
      clearDraft(entryDate);
      return;
    }
    writeDraft(entryDate, { supplements, notes, ts: Date.now() });
  }, [supplements, notes, savedRef, entryDate, loaded]);

  const save = async () => {
    setSaving(true);
    try {
      await endpoints.upsertDailyRoutine({ entry_date: entryDate, supplements, notes });
      toast.success("Saved.");
      setDirty(false);
      setRestoredFromDraft(false);
      setSavedRef({ supplements, notes });
      clearDraft(entryDate);
      const { data: all } = await endpoints.listDailyRoutine(14);
      setHistory(all);
    } catch {
      toast.error("Could not save.");
    } finally { setSaving(false); }
  };

  const revert = () => {
    setSupplements(savedRef.supplements);
    setNotes(savedRef.notes);
    setDirty(false);
    setRestoredFromDraft(false);
    clearDraft(entryDate);
    toast("Reverted to last saved.");
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Daily Routine"
        italic="one small day at a time"
        blurb="Log the supplements you took and a note or two on how the day landed."
      />

      <div className="p-6 border hairline rounded-md bg-[hsl(var(--paper))] space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Date</label>
          <Input
            type="date"
            value={entryDate}
            onChange={(e) => { setEntryDate(e.target.value); }}
            data-testid="routine-date"
            className="w-auto bg-transparent border-[hsl(var(--border))]"
          />
        </div>

        <HabitReminders />

        {restoredFromDraft && (
          <div
            className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-md bg-[hsl(var(--moss))]/12 border border-[hsl(var(--moss))]/30 text-sm"
            data-testid="routine-draft-banner"
            role="status"
          >
            <span>
              <span className="font-medium">Draft restored</span>
              <span className="text-[hsl(var(--muted-foreground))]"> — you had unsaved changes from before.</span>
            </span>
            <button
              onClick={revert}
              data-testid="routine-revert-draft"
              className="text-xs underline underline-offset-4 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))]"
            >
              revert to last saved
            </button>
          </div>
        )}

        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-2">Supplements taken</div>
          <Textarea
            value={supplements}
            onChange={(e) => { setSupplements(e.target.value); setDirty(true); setRestoredFromDraft(false); }}
            data-testid="routine-supplements"
            placeholder="Vitamin D 2000IU&#10;Magnesium 400mg&#10;Omega-3 1g"
            className="bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--moss))] min-h-[130px] leading-relaxed"
          />
          <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">One per line, freely.</div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-2">Notes & reflections</div>
          <Textarea
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setDirty(true); setRestoredFromDraft(false); }}
            data-testid="routine-notes"
            placeholder="What did you notice about your body, your mood, your energy?"
            className="bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--moss))] min-h-[110px] leading-relaxed"
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="routine-save-status">
            {dirty ? "Unsaved changes — draft is safe on this device" : "Saved"}
          </div>
          <Button
            onClick={save}
            disabled={saving || !dirty}
            data-testid="routine-save"
            className="rounded-full bg-[hsl(var(--moss))] hover:bg-[hsl(var(--moss))]/90 text-[hsl(var(--paper))]"
          >
            <Save size={13} className="mr-1.5" /> {saving ? "Saving…" : "Save the day"}
          </Button>
        </div>
      </div>

      {history.length > 0 && (
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Recent days</div>
          <ul className="grid md:grid-cols-2 gap-3" data-testid="routine-history">
            {history.map((h) => (
              <li
                key={h.id}
                className={`p-4 border hairline rounded-md bg-[hsl(var(--paper))] tab-link cursor-pointer ${entryDate === h.entry_date ? "ring-1 ring-[hsl(var(--moss))]/50" : ""}`}
                onClick={() => setEntryDate(h.entry_date)}
                data-testid={`routine-history-${h.entry_date}`}
              >
                <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">{prettyDate(h.entry_date)}</div>
                {h.supplements && (
                  <div className="mt-1 text-sm whitespace-pre-wrap line-clamp-3">{h.supplements}</div>
                )}
                {h.notes && (
                  <div className="mt-1 text-xs italic text-[hsl(var(--muted-foreground))] line-clamp-2">{h.notes}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ==================================== FITNESS ================================= */
function FitnessSection() {
  const [entries, setEntries] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [profile, setProfile] = useState(null);
  const [openGoal, setOpenGoal] = useState(false);
  const [restDay, setRestDay] = useState(null);
  const [restDayDismissed, setRestDayDismissed] = useState(() => {
    try { return sessionStorage.getItem("rest-day-dismissed") || ""; } catch { return ""; }
  });

  const checkRestDay = async () => {
    try {
      const { data } = await endpoints.fitnessRestDayCheck();
      setRestDay(data);
    } catch { /* silent */ }
  };

  const load = async () => {
    const [{ data: es }, { data: w }, { data: p }] = await Promise.all([
      endpoints.fitnessEntries(200),
      endpoints.fitnessWeeklySummary(8),
      endpoints.profile(),
    ]);
    setEntries(es);
    setWeekly(w);
    setProfile(p);
    checkRestDay();
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    const prev = entries;
    setEntries((e) => e.filter((x) => x.id !== id));
    try { await endpoints.deleteFitnessEntry(id); toast("Removed."); }
    catch { setEntries(prev); toast.error("Could not remove."); }
  };

  const dismissRestDay = () => {
    const key = new Date().toISOString().slice(0, 10);
    try { sessionStorage.setItem("rest-day-dismissed", key); } catch { /* noop */ }
    setRestDayDismissed(key);
  };

  const byDate = useMemo(() => groupByDate(entries), [entries]);
  const showRestDayPill =
    restDay?.triggered &&
    restDayDismissed !== new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Fitness"
        italic="one honest set at a time"
        blurb="Log a single exercise per entry — the sets tell their own story."
        action={
          <AddFitnessDialog
            open={openAdd}
            onOpenChange={setOpenAdd}
            onCreated={load}
            editing={editingEntry}
            onEditDone={() => { setEditingEntry(null); setOpenAdd(false); load(); }}
          />
        }
      />
      {showRestDayPill && (
        <RestDayPill stats={restDay} onDismiss={dismissRestDay} />
      )}
      <WeeklyFitnessRollup
        weeks={weekly}
        goal={profile?.fitness_weekly_volume_goal || null}
        goalUnit={profile?.fitness_weekly_volume_unit || "lb"}
        onEditGoal={() => setOpenGoal(true)}
      />
      <FitnessGoalDialog
        open={openGoal}
        onOpenChange={setOpenGoal}
        current={profile?.fitness_weekly_volume_goal || ""}
        currentUnit={profile?.fitness_weekly_volume_unit || "lb"}
        onSaved={load}
      />
      {entries.length === 0 && (
        <EmptyState message="No entries yet. Log your first set — even one line is a beginning." />
      )}
      {Object.keys(byDate).map((day) => (
        <div key={day} className="space-y-2" data-testid={`fitness-day-${day}`}>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">{prettyDate(day)}</div>
          <ul className="grid md:grid-cols-2 gap-3">
            {byDate[day].map((e) => (
              <li key={e.id} className="p-4 border hairline rounded-md bg-[hsl(var(--paper))] flex items-start gap-3" data-testid={`fitness-entry-${e.id}`}>
                <div className="w-9 h-9 rounded-full bg-[hsl(var(--clay))]/12 grid place-items-center shrink-0">
                  <Dumbbell size={14} className="text-[hsl(var(--clay))]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-display text-lg leading-tight">{e.exercise_type}</div>
                    {e.entry_time && <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{e.entry_time}</div>}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm">
                    {e.reps != null && <span><span className="text-[hsl(var(--muted-foreground))]">Reps</span> · {e.reps}</span>}
                    {e.weight != null && <span><span className="text-[hsl(var(--muted-foreground))]">Weight</span> · {e.weight}{e.weight_unit ? ` ${e.weight_unit}` : ""}</span>}
                  </div>
                  {e.notes && <p className="mt-1 text-xs italic text-[hsl(var(--muted-foreground))]">{e.notes}</p>}
                </div>
                <LogReminderControl
                  room="fitness"
                  refId={e.id}
                  refLabel={e.exercise_type}
                  testid={`fitness-reminder-${e.id}`}
                />
                <button
                  onClick={() => { setEditingEntry(e); setOpenAdd(true); }}
                  aria-label="Edit entry"
                  data-testid={`fitness-edit-${e.id}`}
                  className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--clay))]"
                >
                  <Pencil size={13} />
                </button>
                <button
                  onClick={() => remove(e.id)}
                  aria-label="Remove entry"
                  data-testid={`fitness-remove-${e.id}`}
                  className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))]"
                >
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function AddFitnessDialog({ open, onOpenChange, onCreated, editing, onEditDone }) {
  const [form, setForm] = useState(blankFitness);
  const [saving, setSaving] = useState(false);
  const isEdit = !!editing;
  const draft = useFormDraft(FITNESS_DRAFT_KEY, form, setForm, blankFitness(), { active: open && !isEdit });

  // When entering edit mode, hydrate the form from the existing entry.
  useEffect(() => {
    if (isEdit) {
      setForm({
        exercise_type: editing.exercise_type || "",
        reps: editing.reps ?? "",
        weight: editing.weight ?? "",
        weight_unit: editing.weight_unit || "lb",
        entry_date: editing.entry_date || "",
        entry_time: editing.entry_time || "",
        notes: editing.notes || "",
      });
    }
    // Reset to blank when the dialog closes.
    if (!open && !isEdit) setForm(blankFitness());
  }, [isEdit, editing, open]);

  const save = async () => {
    if (!form.exercise_type.trim()) { toast.error("Add an exercise name."); return; }
    setSaving(true);
    try {
      const payload = {
        exercise_type: form.exercise_type.trim(),
        entry_date: form.entry_date || undefined,
        entry_time: form.entry_time || undefined,
        reps: form.reps === "" ? null : Number(form.reps),
        weight: form.weight === "" ? null : Number(form.weight),
        weight_unit: form.weight_unit,
        notes: form.notes,
      };
      if (isEdit) {
        await endpoints.updateFitnessEntry(editing.id, payload);
        toast.success("Updated.");
        onEditDone?.();
      } else {
        await endpoints.createFitnessEntry(payload);
        setForm(blankFitness());
        draft.clear();
        onOpenChange(false);
        onCreated?.();
        toast.success("Logged.");
      }
    } catch { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  const handleOpenChange = (v) => {
    if (!v && isEdit) onEditDone?.();
    else onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="fitness-add-open" className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]">
          <Plus size={13} className="mr-1.5" /> Log an exercise
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[hsl(var(--paper))] max-w-md" data-testid="fitness-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Edit exercise" : "Log an exercise"}</DialogTitle>
          <DialogDescription>{isEdit ? "Refine what you logged." : "One movement at a time."}</DialogDescription>
        </DialogHeader>
        {draft.restored && !isEdit && (
          <DraftBanner
            testid="fitness-draft-banner"
            onDismiss={() => { setForm(blankFitness()); draft.clear(); }}
          />
        )}
        <div className="space-y-3">
          <Field label="Exercise">
            <Input value={form.exercise_type} onChange={(e) => setForm({ ...form, exercise_type: e.target.value })} data-testid="fitness-exercise" placeholder="e.g. Deadlift, Vinyasa flow" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Reps"><Input type="number" min="0" value={form.reps} onChange={(e) => setForm({ ...form, reps: e.target.value })} data-testid="fitness-reps" /></Field>
            <Field label="Weight">
              <div className="flex gap-2">
                <Input type="number" step="0.5" min="0" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} data-testid="fitness-weight" />
                <select
                  value={form.weight_unit}
                  onChange={(e) => setForm({ ...form, weight_unit: e.target.value })}
                  data-testid="fitness-weight-unit"
                  className="rounded-md border hairline px-2 bg-transparent text-sm"
                >
                  <option value="lb">lb</option>
                  <option value="kg">kg</option>
                </select>
              </div>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} data-testid="fitness-date" /></Field>
            <Field label="Time"><Input type="time" value={form.entry_time} onChange={(e) => setForm({ ...form, entry_time: e.target.value })} data-testid="fitness-time" /></Field>
          </div>
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="fitness-notes" placeholder="How did it feel?" className="min-h-[70px]" /></Field>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="fitness-save" className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Save entry")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const blankFitness = () => ({ exercise_type: "", reps: "", weight: "", weight_unit: "lb", entry_date: "", entry_time: "", notes: "" });

/* ==================================== PEPTIDES ================================ */
function PeptidesSection() {
  const [entries, setEntries] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [openCycle, setOpenCycle] = useState(false);
  const [cycleFilter, setCycleFilter] = useState(null); // cycle id or null (all)
  const [editingEntry, setEditingEntry] = useState(null);

  const load = async () => {
    const [{ data: es }, { data: cs }] = await Promise.all([
      endpoints.peptideEntries(300),
      endpoints.peptideCycles(),
    ]);
    setEntries(es);
    setCycles(cs);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    const prev = entries;
    setEntries((e) => e.filter((x) => x.id !== id));
    try { await endpoints.deletePeptideEntry(id); toast("Removed."); load(); }
    catch { setEntries(prev); toast.error("Could not remove."); }
  };

  const removeCycle = async (id) => {
    try { await endpoints.deletePeptideCycle(id); toast("Cycle removed."); load(); }
    catch { toast.error("Could not remove cycle."); }
  };

  const visibleEntries = useMemo(
    () => (cycleFilter ? entries.filter((e) => e.cycle_id === cycleFilter) : entries),
    [entries, cycleFilter],
  );
  const byDate = useMemo(() => groupByDate(visibleEntries), [visibleEntries]);
  const activeCycle = cycleFilter ? cycles.find((c) => c.id === cycleFilter) : null;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Peptides"
        italic="every injection a small ceremony"
        blurb="Track each injection, group them into cycles, and watch the pattern emerge."
        action={
          <div className="flex gap-2 flex-wrap">
            <NewCycleDialog open={openCycle} onOpenChange={setOpenCycle} onCreated={load} />
            <AddPeptideDialog
              open={openAdd}
              onOpenChange={setOpenAdd}
              cycles={cycles}
              onCreated={load}
              editing={editingEntry}
              onEditDone={() => { setEditingEntry(null); setOpenAdd(false); load(); }}
            />
          </div>
        }
      />

      <CyclesStrip
        cycles={cycles}
        activeId={cycleFilter}
        onSelect={(id) => setCycleFilter((cur) => (cur === id ? null : id))}
        onClear={() => setCycleFilter(null)}
        onRemove={removeCycle}
      />

      {activeCycle && <CycleDetail cycle={activeCycle} />}

      {visibleEntries.length === 0 && (
        <EmptyState message={cycleFilter ? "No injections logged in this cycle yet." : "No injections logged yet. When you're ready, log the first one."} />
      )}
      {Object.keys(byDate).map((day) => (
        <div key={day} className="space-y-2" data-testid={`peptide-day-${day}`}>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">{prettyDate(day)}</div>
          <ul className="space-y-3">
            {byDate[day].map((e) => (
              <li key={e.id} className="p-5 border hairline rounded-md bg-[hsl(var(--paper))]" data-testid={`peptide-entry-${e.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-[hsl(var(--terracotta))]/15 grid place-items-center shrink-0">
                      <Syringe size={14} className="text-[hsl(var(--terracotta))]" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-lg leading-tight flex items-center gap-2 flex-wrap">
                        {e.peptide_name}
                        {e.cycle_id && (
                          <CycleTag cycles={cycles} id={e.cycle_id} />
                        )}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
                        {e.entry_time || "no time"} · {e.injection_site || "site not noted"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <LogReminderControl
                      room="peptide"
                      refId={e.id}
                      refLabel={e.peptide_name}
                      testid={`peptide-reminder-${e.id}`}
                    />
                    <button
                      onClick={() => { setEditingEntry(e); setOpenAdd(true); }}
                      aria-label="Edit entry"
                      data-testid={`peptide-edit-${e.id}`}
                      className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--clay))]"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => remove(e.id)}
                      aria-label="Remove entry"
                      data-testid={`peptide-remove-${e.id}`}
                      className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))]"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <StatCell label="BAC water" value={e.bac_water_ml != null ? `${e.bac_water_ml} ml` : "—"} />
                  <StatCell label="Injected" value={e.injected_amount != null ? `${e.injected_amount} ${e.injected_unit || ""}` : "—"} />
                  <StatCell label="Site" value={e.injection_site || "—"} />
                  <StatCell label="Side effects" value={e.side_effects || "None"} />
                </div>
                {e.notes && <p className="mt-3 text-xs italic text-[hsl(var(--muted-foreground))]">{e.notes}</p>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function CyclesStrip({ cycles, activeId, onSelect, onClear, onRemove }) {
  if (!cycles || cycles.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="cycles-strip">
      <div className="flex items-baseline justify-between">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Cycles</div>
        {activeId && (
          <button
            onClick={onClear}
            data-testid="cycles-clear"
            className="text-xs underline underline-offset-4 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--clay))]"
          >
            show all injections
          </button>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {cycles.map((c) => {
          const active = c.id === activeId;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c.id)}
              data-testid={`cycle-card-${c.id}`}
              aria-pressed={active}
              className={`text-left p-4 border rounded-md tab-link ${
                active
                  ? "bg-[hsl(var(--terracotta))]/10 border-[hsl(var(--terracotta))]/50 shadow-[inset_3px_0_0_hsl(var(--terracotta))]"
                  : "hairline hover:bg-[hsl(var(--sand))]/40 bg-[hsl(var(--paper))]"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display text-lg leading-tight">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mt-0.5">
                    {c.peptide_name ? `${c.peptide_name} · ` : ""}{prettyDate(c.start_date)}{c.end_date ? ` → ${prettyDate(c.end_date)}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove cycle "${c.name}"? Injections stay, just detached.`)) onRemove(c.id); }}
                  aria-label="Remove cycle"
                  data-testid={`cycle-remove-${c.id}`}
                  className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))]"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
                <span>
                  <span className="font-display text-xl">{c.entries_count}</span>{" "}
                  <span className="text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-[0.18em]">injections</span>
                </span>
                {Object.entries(c.totals_by_unit || {}).map(([unit, total]) => (
                  <span key={unit}>
                    <span className="font-display text-xl">{total}</span>{" "}
                    <span className="text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-[0.18em]">{unit} total</span>
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CycleDetail({ cycle }) {
  const [days, remaining] = useMemo(() => cycleDayInfo(cycle), [cycle]);
  return (
    <div
      className="p-4 border hairline rounded-md bg-[hsl(var(--terracotta))]/5 text-sm"
      data-testid={`cycle-detail-${cycle.id}`}
    >
      <span className="text-[hsl(var(--muted-foreground))]">Filtering to </span>
      <span className="font-medium">{cycle.name}</span>
      <span className="text-[hsl(var(--muted-foreground))]"> · day </span>
      <span className="font-medium">{days}</span>
      {cycle.end_date && (
        <>
          <span className="text-[hsl(var(--muted-foreground))]"> of </span>
          <span className="font-medium">{days + remaining}</span>
          {remaining > 0 && (
            <span className="text-[hsl(var(--muted-foreground))]"> · {remaining} left</span>
          )}
          {remaining === 0 && (
            <span className="text-[hsl(var(--terracotta))]"> · complete</span>
          )}
        </>
      )}
      {cycle.notes && <div className="mt-2 italic text-[hsl(var(--muted-foreground))]">{cycle.notes}</div>}
    </div>
  );
}

function CycleTag({ cycles, id }) {
  const c = cycles.find((x) => x.id === id);
  if (!c) return null;
  return (
    <span
      className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-full bg-[hsl(var(--terracotta))]/12 text-[hsl(var(--terracotta))]"
      data-testid={`peptide-entry-cycle-tag`}
    >
      {c.name}
    </span>
  );
}

function cycleDayInfo(cycle) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(cycle.start_date + "T00:00:00");
  const dayNum = Math.max(1, Math.floor((today - start) / 86400000) + 1);
  let remaining = 0;
  if (cycle.end_date) {
    const end = new Date(cycle.end_date + "T00:00:00");
    remaining = Math.max(0, Math.floor((end - today) / 86400000));
  }
  return [dayNum, remaining];
}

function NewCycleDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(blankCycle);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name your cycle."); return; }
    if (!form.start_date) { toast.error("Pick a start date."); return; }
    setSaving(true);
    try {
      await endpoints.createPeptideCycle({
        name: form.name.trim(),
        peptide_name: form.peptide_name.trim(),
        start_date: form.start_date,
        end_date: form.end_date || null,
        notes: form.notes,
      });
      setForm(blankCycle());
      onOpenChange(false);
      onCreated?.();
      toast.success("Cycle started.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save cycle.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="cycle-add-open"
          className="rounded-full border-[hsl(var(--terracotta))]/50 text-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/10"
        >
          <Plus size={13} className="mr-1.5" /> Start a cycle
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[hsl(var(--paper))] max-w-md" data-testid="cycle-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Start a cycle</DialogTitle>
          <DialogDescription>A named window for a peptide program.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cycle-name" placeholder="4-week BPC-157" /></Field>
          <Field label="Peptide (optional)"><Input value={form.peptide_name} onChange={(e) => setForm({ ...form, peptide_name: e.target.value })} data-testid="cycle-peptide" placeholder="BPC-157" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="cycle-start" /></Field>
            <Field label="End date (optional)"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="cycle-end" /></Field>
          </div>
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cycle-notes" placeholder="Protocol, dosage, source…" className="min-h-[70px]" /></Field>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="cycle-save" className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--paper))]">
            {saving ? "Saving…" : "Save cycle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const blankCycle = () => ({ name: "", peptide_name: "", start_date: new Date().toISOString().slice(0, 10), end_date: "", notes: "" });

function AddPeptideDialog({ open, onOpenChange, onCreated, cycles = [], editing, onEditDone }) {
  const [form, setForm] = useState(blankPeptide);
  const [saving, setSaving] = useState(false);
  const isEdit = !!editing;
  const draft = useFormDraft(PEPTIDE_DRAFT_KEY, form, setForm, blankPeptide(), { active: open && !isEdit });

  useEffect(() => {
    if (isEdit) {
      setForm({
        peptide_name: editing.peptide_name || "",
        bac_water_ml: editing.bac_water_ml ?? "",
        injected_amount: editing.injected_amount ?? "",
        injected_unit: editing.injected_unit || "mg",
        injection_site: editing.injection_site || "",
        side_effects: editing.side_effects || "",
        entry_date: editing.entry_date || "",
        entry_time: editing.entry_time || "",
        notes: editing.notes || "",
        cycle_id: editing.cycle_id || "",
      });
    }
    if (!open && !isEdit) setForm(blankPeptide());
  }, [isEdit, editing, open]);

  const save = async () => {
    if (!form.peptide_name.trim()) { toast.error("Add a peptide name."); return; }
    setSaving(true);
    try {
      const payload = {
        peptide_name: form.peptide_name.trim(),
        entry_date: form.entry_date || undefined,
        entry_time: form.entry_time || undefined,
        bac_water_ml: form.bac_water_ml === "" ? null : Number(form.bac_water_ml),
        injected_amount: form.injected_amount === "" ? null : Number(form.injected_amount),
        injected_unit: form.injected_unit,
        injection_site: form.injection_site,
        side_effects: form.side_effects,
        notes: form.notes,
        cycle_id: form.cycle_id || null,
      };
      if (isEdit) {
        await endpoints.updatePeptideEntry(editing.id, payload);
        toast.success("Updated.");
        onEditDone?.();
      } else {
        await endpoints.createPeptideEntry(payload);
        setForm(blankPeptide());
        draft.clear();
        onOpenChange(false);
        onCreated?.();
        toast.success("Logged.");
      }
    } catch { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  const handleOpenChange = (v) => {
    if (!v && isEdit) onEditDone?.();
    else onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="peptide-add-open" className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--paper))]">
          <Plus size={13} className="mr-1.5" /> Log an injection
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[hsl(var(--paper))] max-w-lg max-h-[92vh] overflow-y-auto" data-testid="peptide-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Edit injection" : "Log an injection"}</DialogTitle>
          <DialogDescription>{isEdit ? "Refine what you logged." : "Every detail helps you notice the pattern."}</DialogDescription>
        </DialogHeader>
        {draft.restored && !isEdit && (
          <DraftBanner
            testid="peptide-draft-banner"
            onDismiss={() => { setForm(blankPeptide()); draft.clear(); }}
          />
        )}
        <div className="space-y-3">
          <Field label="Peptide name">
            <Input value={form.peptide_name} onChange={(e) => setForm({ ...form, peptide_name: e.target.value })} data-testid="peptide-name" placeholder="e.g. BPC-157, Thymosin-Alpha" />
          </Field>
          {cycles.length > 0 && (
            <Field label="Cycle (optional)">
              <select
                value={form.cycle_id}
                onChange={(e) => setForm({ ...form, cycle_id: e.target.value })}
                data-testid="peptide-cycle-select"
                className="w-full rounded-md border hairline px-3 py-2 bg-transparent text-sm"
              >
                <option value="">— no cycle —</option>
                {cycles.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="BAC water (ml)"><Input type="number" step="0.1" min="0" value={form.bac_water_ml} onChange={(e) => setForm({ ...form, bac_water_ml: e.target.value })} data-testid="peptide-bac" /></Field>
            <Field label="Injected amount">
              <div className="flex gap-2">
                <Input type="number" step="0.1" min="0" value={form.injected_amount} onChange={(e) => setForm({ ...form, injected_amount: e.target.value })} data-testid="peptide-amount" />
                <select
                  value={form.injected_unit}
                  onChange={(e) => setForm({ ...form, injected_unit: e.target.value })}
                  data-testid="peptide-unit"
                  className="rounded-md border hairline px-2 bg-transparent text-sm"
                >
                  <option value="IU">IU</option>
                  <option value="mcg">mcg</option>
                  <option value="mg">mg</option>
                  <option value="ml">ml</option>
                </select>
              </div>
            </Field>
          </div>
          <Field label="Injection site"><Input value={form.injection_site} onChange={(e) => setForm({ ...form, injection_site: e.target.value })} data-testid="peptide-site" placeholder="left abdomen, right thigh…" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} data-testid="peptide-date" /></Field>
            <Field label="Time"><Input type="time" value={form.entry_time} onChange={(e) => setForm({ ...form, entry_time: e.target.value })} data-testid="peptide-time" /></Field>
          </div>
          <Field label="Side effects"><Textarea value={form.side_effects} onChange={(e) => setForm({ ...form, side_effects: e.target.value })} data-testid="peptide-side" placeholder="Anything you noticed — including 'none'." className="min-h-[60px]" /></Field>
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="peptide-notes" placeholder="How you feel, batch, provider…" className="min-h-[70px]" /></Field>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="peptide-save" className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--paper))]">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Save entry")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const blankPeptide = () => ({
  peptide_name: "", bac_water_ml: "", injected_amount: "", injected_unit: "IU",
  injection_site: "", entry_date: "", entry_time: "", side_effects: "", notes: "",
  cycle_id: "",
});

/* ================================== PROCEDURES ================================ */
function ProceduresSection() {
  const [procedures, setProcedures] = useState([]);
  const [meta, setMeta] = useState({ categories: [] });
  const [openAdd, setOpenAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [{ data: procs }, { data: m }] = await Promise.all([
      endpoints.procedures(),
      endpoints.proceduresMeta(),
    ]);
    setProcedures(procs);
    setMeta(m);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    const prev = procedures;
    setProcedures((p) => p.filter((x) => x.id !== id));
    try { await endpoints.deleteProcedure(id); toast("Removed."); }
    catch { setProcedures(prev); toast.error("Could not remove."); }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Procedures"
        italic="the record of what you've done"
        blurb="Aesthetic, IV, peptide programs — a clear record of every session."
        action={<AddProcedureDialog
          open={openAdd}
          onOpenChange={setOpenAdd}
          categories={meta.categories}
          onCreated={load}
          editing={editing}
          onEditDone={() => { setEditing(null); setOpenAdd(false); load(); }}
        />}
      />
      {procedures.length === 0 && <EmptyState message="No procedures logged yet." />}
      <ul className="grid md:grid-cols-2 gap-3">
        {procedures.map((p) => {
          const Icon = CAT_ICON[p.category] || Circle;
          return (
            <li key={p.id} className="p-5 border hairline rounded-md bg-[hsl(var(--paper))] flex items-start gap-3" data-testid={`procedure-${p.id}`}>
              <div className="w-9 h-9 rounded-full bg-[hsl(var(--clay))]/12 grid place-items-center shrink-0">
                <Icon size={14} className="text-[hsl(var(--clay))]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg leading-tight">{p.name}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
                  {p.category?.replaceAll("_", " ") || "other"} · {prettyDate(p.procedure_date)}
                </div>
                {p.provider && (
                  <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">Provider · {p.provider}</div>
                )}
                {p.notes && <p className="mt-2 text-sm italic text-[hsl(var(--muted-foreground))]">{p.notes}</p>}
                {p.next_visit_date && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--clay))] bg-[hsl(var(--clay))]/10 px-2 py-1 rounded-full">
                    Next · {prettyDate(p.next_visit_date)}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <LogReminderControl
                  room="procedure"
                  refId={p.id}
                  refLabel={p.name}
                  testid={`procedure-reminder-${p.id}`}
                />
                <button
                  onClick={() => { setEditing(p); setOpenAdd(true); }}
                  aria-label="Edit procedure"
                  data-testid={`procedure-edit-${p.id}`}
                  className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--clay))]"
                >
                  <Pencil size={13} />
                </button>
                <button
                  onClick={() => remove(p.id)}
                  aria-label="Remove procedure"
                  data-testid={`procedure-remove-${p.id}`}
                  className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))]"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AddProcedureDialog({ open, onOpenChange, categories, onCreated, editing, onEditDone }) {
  const blank = { name: "", category: "other", procedure_date: "", provider: "", notes: "", next_visit_date: "" };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const isEdit = !!editing;

  useEffect(() => {
    if (isEdit) {
      setForm({
        name: editing.name || "",
        category: editing.category || "other",
        procedure_date: editing.procedure_date || "",
        provider: editing.provider || "",
        notes: editing.notes || "",
        next_visit_date: editing.next_visit_date || "",
      });
    }
    if (!open && !isEdit) setForm(blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editing, open]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Add a procedure name."); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        procedure_date: form.procedure_date || undefined,
        notes: form.provider ? `Provider: ${form.provider}\n${form.notes}` : form.notes,
        next_visit_date: form.next_visit_date || null,
      };
      if (isEdit) {
        await endpoints.updateProcedure(editing.id, payload);
        toast.success("Updated.");
        onEditDone?.();
      } else {
        await endpoints.createProcedure(payload);
        setForm(blank);
        onOpenChange(false);
        onCreated?.();
        toast.success("Logged.");
      }
    } catch { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  const handleOpenChange = (v) => {
    if (!v && isEdit) onEditDone?.();
    else onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="procedure-add-open" className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]">
          <Plus size={13} className="mr-1.5" /> Add a procedure
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[hsl(var(--paper))] max-w-lg" data-testid="procedure-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Edit procedure" : "Add a procedure"}</DialogTitle>
          <DialogDescription>{isEdit ? "Refine what you logged." : "Kept quietly for your own record."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Procedure name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="procedure-name" placeholder="Microneedling, IV drip…" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="procedure-category"
                className="w-full rounded-md border hairline px-3 py-2 bg-transparent text-sm"
              >
                {(categories || []).map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Date received"><Input type="date" value={form.procedure_date} onChange={(e) => setForm({ ...form, procedure_date: e.target.value })} data-testid="procedure-date" /></Field>
          </div>
          <Field label="Provider or wellness practice (optional)"><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} data-testid="procedure-provider" placeholder="Sanctuary Wellness, Dr. Chen…" /></Field>
          <Field label="Next visit (optional)"><Input type="date" value={form.next_visit_date} onChange={(e) => setForm({ ...form, next_visit_date: e.target.value })} data-testid="procedure-next-visit" /></Field>
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="procedure-notes" placeholder="Any details worth remembering." className="min-h-[70px]" /></Field>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="procedure-save" className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Save procedure")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ================================== HELPERS =================================== */
function SectionHeader({ title, italic, blurb, action }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">{title}</div>
        <h2 className="font-display text-4xl mt-1 tracking-tight leading-[1.05]">
          {title.split(" ")[0]} <span className="italic">{italic}</span>
        </h2>
        {blurb && <p className="mt-2 max-w-xl text-sm text-[hsl(var(--muted-foreground))]">{blurb}</p>}
      </div>
      {action}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{label}</span>
      {children}
    </label>
  );
}

function StatCell({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className="text-sm truncate">{value}</div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="p-8 border border-dashed hairline rounded-md bg-[hsl(var(--paper))] text-center text-sm text-[hsl(var(--muted-foreground))]">
      {message}
    </div>
  );
}

function RestDayPill({ stats, onDismiss }) {
  const count = stats?.high_volume_count ?? 0;
  const window = stats?.lookback_days ?? 7;
  return (
    <div
      data-testid="rest-day-pill"
      className="relative p-4 border hairline rounded-md bg-[hsl(var(--moss))]/8 flex items-start gap-3"
    >
      <div className="w-9 h-9 rounded-full bg-[hsl(var(--moss))]/18 grid place-items-center shrink-0">
        <Moon size={15} className="text-[hsl(var(--moss))]" />
      </div>
      <div className="flex-1 min-w-0 pr-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--moss))]">
          Rest-day signal
        </div>
        <div className="font-display text-lg leading-tight mt-1">Consider a rest day</div>
        <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          {count} strong day{count === 1 ? "" : "s"} in the last {window}. Rest is essential for
          healing and rebuilding — let the work land.
        </p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss rest-day signal"
        data-testid="rest-day-dismiss"
        className="absolute top-3 right-3 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function WeeklyFitnessRollup({ weeks, goal, goalUnit = "lb", onEditGoal }) {
  if (!weeks || weeks.length === 0) return null;
  const hasLb = weeks.some((w) => w.volume_lb > 0);
  const hasKg = weeks.some((w) => w.volume_kg > 0);
  // Nothing to show at all — no volume and no goal
  if (!hasLb && !hasKg && !goal) return null;
  const useLb = goal ? goalUnit === "lb" : hasLb || !hasKg;
  const maxLb = Math.max(...weeks.map((w) => w.volume_lb), goal && goalUnit === "lb" ? goal : 1);
  const maxKg = Math.max(...weeks.map((w) => w.volume_kg), goal && goalUnit === "kg" ? goal : 1);
  const totalLb = weeks.reduce((s, w) => s + (w.volume_lb || 0), 0);
  const totalKg = weeks.reduce((s, w) => s + (w.volume_kg || 0), 0);
  const cur = weeks[weeks.length - 1];
  const prev = weeks[weeks.length - 2] || { volume_lb: 0, volume_kg: 0 };
  const curV = useLb ? cur.volume_lb : cur.volume_kg;
  const prevV = useLb ? prev.volume_lb : prev.volume_kg;
  const delta = prevV > 0 ? Math.round(((curV - prevV) / prevV) * 100) : null;
  const goalPct = goal ? Math.min(999, Math.round((curV / goal) * 100)) : null;

  return (
    <div className="p-5 border hairline rounded-md bg-[hsl(var(--paper))]" data-testid="fitness-weekly-rollup">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Weekly volume</div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            {hasLb && (
              <span>
                <span className="font-display text-3xl">{totalLb.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                <span className="text-sm text-[hsl(var(--muted-foreground))] ml-1">lb · 8 wk</span>
              </span>
            )}
            {hasKg && (
              <span>
                <span className="font-display text-3xl">{totalKg.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                <span className="text-sm text-[hsl(var(--muted-foreground))] ml-1">kg · 8 wk</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {delta !== null && curV > 0 && (
            <div
              className={`text-sm px-3 py-1 rounded-full ${
                delta > 0
                  ? "bg-[hsl(var(--moss))]/15 text-[hsl(var(--moss))]"
                  : delta < 0
                    ? "bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--terracotta))]"
                    : "bg-[hsl(var(--sand))] text-[hsl(var(--muted-foreground))]"
              }`}
              data-testid="fitness-weekly-trend"
            >
              {delta > 0 ? "▲" : delta < 0 ? "▼" : "·"} {Math.abs(delta)}% vs last week
            </div>
          )}
          <button
            type="button"
            onClick={onEditGoal}
            data-testid="fitness-goal-edit"
            className="text-xs px-3 py-1 rounded-full border hairline text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--sand))] tab-link"
          >
            {goal ? "Edit goal" : "Set weekly goal"}
          </button>
        </div>
      </div>

      {goal && (
        <div className="mb-4" data-testid="fitness-goal-progress">
          <div className="flex items-baseline justify-between text-xs mb-1">
            <span className="text-[hsl(var(--muted-foreground))] uppercase tracking-[0.2em] text-[10px]">This week</span>
            <span className="tabular-nums">
              <span className="font-medium">{curV.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              <span className="text-[hsl(var(--muted-foreground))]"> / {goal.toLocaleString(undefined, { maximumFractionDigits: 0 })} {goalUnit}</span>
              <span className={`ml-2 ${goalPct >= 100 ? "text-[hsl(var(--moss))]" : "text-[hsl(var(--muted-foreground))]"}`}>
                {goalPct}%{goalPct >= 100 ? " · reached" : ""}
              </span>
            </span>
          </div>
          <div className="h-2 rounded-full bg-[hsl(var(--sand))] overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${goalPct >= 100 ? "bg-[hsl(var(--moss))]" : "bg-[hsl(var(--clay))]"}`}
              style={{ width: `${Math.min(100, goalPct)}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-8 gap-1.5 items-end" data-testid="fitness-weekly-bars">
        {weeks.map((w) => {
          const primary = useLb ? w.volume_lb : w.volume_kg;
          const max = useLb ? maxLb : maxKg;
          const pct = max > 0 ? Math.max(2, Math.round((primary / max) * 100)) : 2;
          const isCur = w.week_start === cur.week_start;
          const hitGoal = goal && primary >= goal;
          const states = w.day_states || [];
          const restDays = w.rest_days || 0;
          const activeDays = w.active_days || 0;
          return (
            <div key={w.week_start} className="flex flex-col items-center gap-1 min-w-0">
              {/* bar sits inside a fixed-height slot so recovery dots align across weeks */}
              <div className="h-24 w-full flex flex-col justify-end">
                <div
                  className={`w-full rounded-t ${
                    hitGoal
                      ? "bg-[hsl(var(--moss))]/80"
                      : isCur
                        ? "bg-[hsl(var(--clay))]"
                        : "bg-[hsl(var(--clay))]/70"
                  } ${primary === 0 ? "opacity-25" : ""}`}
                  style={{ height: `${pct}%` }}
                  title={`Week of ${w.week_start} · ${primary} ${useLb ? "lb" : "kg"} · ${activeDays} active · ${restDays} rest`}
                />
              </div>
              {/* Recovery strip: one dot per weekday. Moss = rest honored, clay = active, hollow = future. */}
              <div
                className="mt-0.5 grid grid-cols-7 gap-[2px] w-full"
                data-testid={`fitness-week-recovery-${w.week_start}`}
                aria-label={`${activeDays} active, ${restDays} rest days that week`}
                title={`${activeDays} active · ${restDays} rest honored`}
              >
                {states.map((s, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full ${
                      s === "active"
                        ? "bg-[hsl(var(--clay))]/80"
                        : s === "rest"
                          ? "bg-[hsl(var(--moss))]/70"
                          : "bg-[hsl(var(--border))]"
                    }`}
                  />
                ))}
              </div>
              <div className="text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))] truncate w-full text-center">
                {w.week_start.slice(5)}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
          Weekly reps × weight{goal ? " · goal bars turn moss when reached" : ""}
        </div>
        {cur && (cur.rest_days || 0) > 0 && (
          <div
            className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--moss))] inline-flex items-center gap-1"
            data-testid="fitness-rest-honored"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[hsl(var(--moss))]/70" aria-hidden />
            {cur.rest_days} rest {cur.rest_days === 1 ? "day" : "days"} honored this week
          </div>
        )}
      </div>
    </div>
  );
}

function FitnessGoalDialog({ open, onOpenChange, current, currentUnit, onSaved }) {
  const [value, setValue] = useState(String(current || ""));
  const [unit, setUnit] = useState(currentUnit || "lb");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(String(current || "")); setUnit(currentUnit || "lb"); }, [current, currentUnit, open]);

  const save = async () => {
    const goal = value.trim() === "" ? null : Number(value);
    if (goal !== null && (Number.isNaN(goal) || goal <= 0)) {
      toast.error("Goal must be a positive number.");
      return;
    }
    setSaving(true);
    try {
      await endpoints.updateProfile({
        fitness_weekly_volume_goal: goal,
        fitness_weekly_volume_unit: unit,
      });
      toast.success(goal ? "Goal set." : "Goal cleared.");
      onOpenChange(false);
      onSaved?.();
    } catch { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(var(--paper))] max-w-sm" data-testid="fitness-goal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Weekly volume goal</DialogTitle>
          <DialogDescription>
            A steady north star. Leave blank to clear the goal.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Target (reps × weight per week)">
            <div className="flex gap-2">
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="10"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                data-testid="fitness-goal-input"
                placeholder="e.g. 5000"
              />
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                data-testid="fitness-goal-unit"
                className="rounded-md border hairline px-2 bg-transparent text-sm"
              >
                <option value="lb">lb</option>
                <option value="kg">kg</option>
              </select>
            </div>
          </Field>
        </div>
        <DialogFooter>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="fitness-goal-save"
            className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
          >
            {saving ? "Saving…" : "Save goal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DraftBanner({ testid, onDismiss }) {
  return (
    <div
      className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-[hsl(var(--moss))]/12 border border-[hsl(var(--moss))]/30 text-xs"
      data-testid={testid}
      role="status"
    >
      <span>
        <span className="font-medium">Draft restored</span>
        <span className="text-[hsl(var(--muted-foreground))]"> — picked up where you left off.</span>
      </span>
      <button
        type="button"
        onClick={onDismiss}
        data-testid={`${testid}-clear`}
        className="underline underline-offset-4 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))]"
      >
        start fresh
      </button>
    </div>
  );
}

function groupByDate(entries) {
  const out = {};
  for (const e of entries) {
    const k = e.entry_date || "unknown";
    if (!out[k]) out[k] = [];
    out[k].push(e);
  }
  return out;
}

function prettyDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}
